import * as Haptics from 'expo-haptics'
import { forwardRef, useCallback } from 'react'
import type { ComponentPropsWithoutRef, ElementRef } from 'react'
import { Platform, Pressable as RNPressable } from 'react-native'
import type { GestureResponderEvent } from 'react-native'
import Animated, {
	useAnimatedStyle,
	useSharedValue,
	withSpring,
	withTiming,
} from 'react-native-reanimated'

/**
 * How a press feels, everywhere.
 *
 * The thing under the finger gives a little — scales to 0.97 and dims to 0.9 over 120ms — and
 * springs back on release, with a light tap of haptic when the press lands. That one gesture, applied to
 * every pressable, is most of what makes an app feel made rather than assembled. Web gets no
 * haptic and keeps its hover colour instead.
 *
 * Disabled pressables do not move: a control that cannot be pressed should not pretend to be.
 */

const PRESSED_SCALE = 0.97
const PRESSED_OPACITY = 0.9
const PRESS_IN_MS = 120

const canBuzz = Platform.OS !== 'web'

type PressFeedbackOptions = {
	/** Off for controls that are pressed constantly, such as a stepper, where a buzz per tap grates. */
	haptic?: boolean
	disabled?: boolean | null
}

/** For a component that owns its own Pressable: spread `handlers` onto it, `style` onto its Animated.View. */
export const usePressFeedback = ({
	haptic = true,
	disabled,
}: PressFeedbackOptions = {}) => {
	const scale = useSharedValue(1)
	const opacity = useSharedValue(1)

	const style = useAnimatedStyle(() => ({
		transform: [{ scale: scale.value }],
		opacity: opacity.value,
	}))

	const onPressIn = useCallback(() => {
		if (disabled) return
		scale.value = withTiming(PRESSED_SCALE, { duration: PRESS_IN_MS })
		opacity.value = withTiming(PRESSED_OPACITY, { duration: PRESS_IN_MS })
	}, [disabled, scale, opacity])

	// The buzz belongs to the press that lands, not to a finger that starts a scroll on a row.
	const onPress = useCallback(() => {
		if (haptic && canBuzz) {
			Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {})
		}
	}, [haptic])

	const onPressOut = useCallback(() => {
		scale.value = withSpring(1, { damping: 18, stiffness: 320 })
		opacity.value = withTiming(1, { duration: PRESS_IN_MS })
	}, [scale, opacity])

	return { style, handlers: { onPressIn, onPressOut, onPress } }
}

const AnimatedPressable = Animated.createAnimatedComponent(RNPressable)

type PressableProps = ComponentPropsWithoutRef<typeof RNPressable> & PressFeedbackOptions

/**
 * A Pressable that responds. Drop-in for react-native's; the animated wrapper takes the
 * pressable's layout so a `className` with width or flex on it still lays out as before.
 *
 * One thing a `className` cannot do here: opacity. The press feedback is an animated style
 * carrying an `opacity`, and an inline style beats a class — so `opacity-40` on a Pressable is
 * silently ignored, which is how a disabled arrow came to look exactly like a live one. Dim
 * through `style`, which composes after the feedback's and therefore wins.
 */
export const Pressable = forwardRef<ElementRef<typeof RNPressable>, PressableProps>(
	({ haptic = true, disabled, onPressIn, onPressOut, onPress, style, ...props }, ref) => {
		const feedback = usePressFeedback({ haptic, disabled })

		const handlePressIn = (event: GestureResponderEvent) => {
			feedback.handlers.onPressIn()
			onPressIn?.(event)
		}
		const handlePressOut = (event: GestureResponderEvent) => {
			feedback.handlers.onPressOut()
			onPressOut?.(event)
		}
		const handlePress = (event: GestureResponderEvent) => {
			feedback.handlers.onPress()
			onPress?.(event)
		}

		// react-native's Pressable accepts a style function of its pressed state; keep that working.
		const composedStyle =
			typeof style === 'function'
				? (state: Parameters<typeof style>[0]) => [feedback.style, style(state)]
				: [feedback.style, style]

		return (
			<AnimatedPressable
				ref={ref}
				disabled={disabled}
				onPressIn={handlePressIn}
				onPressOut={handlePressOut}
				onPress={handlePress}
				style={composedStyle}
				{...props}
			/>
		)
	}
)
Pressable.displayName = 'Pressable'
